import { FunctionComponent } from 'react'
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, Typography } from '@mui/material'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { IThirdPartyConnectionDetailsCompany } from '../../modules/third-party-connection/shared-services/third-party-connection-details/third-party-connection-details.model';
import { DialogSaveButtons } from '../presentation-components/miscellaneous-components/dialog-save-buttons';

const DATE_FORMAT = 'YYYY-MM-DD'

/** The latest date a sync can read again from: today, or the period end if that is earlier. */
export const latestSyncFromDate = (periodEndDate?: string | Date): string => {
  const today = moment().startOf('day')
  const end = periodEndDate ? moment(periodEndDate).startOf('day') : null
  return (end && end.isBefore(today) ? end : today).format(DATE_FORMAT)
}

/**
 * The Sync Data dialog: which company to read, and optionally a date to read again from.
 * Without a date the sync continues from the last one. With one, the ledger is read again from
 * that date and documents whose emissions were deleted since come back for review; anything
 * pending or still counted is left alone.
 */
export const SelectTenantDialog: FunctionComponent<{
  dialogDetails: IThirdPartyConnectionDetailsCompany[],
  open: boolean,
  closeDialogHandler: () => void,
  submitHandler: (connectionCode: string, tenantId: string, resetForm: () => void, syncFrom: string | null) => void,
  connectionCode: string,
  connectionName: string,
  isSubmitting: boolean,
  periodStartDate?: string | Date,
  periodEndDate?: string | Date,
}> = ({
  dialogDetails,
  open,
  closeDialogHandler,
  submitHandler,
  connectionCode,
  connectionName,
  isSubmitting,
  periodStartDate,
  periodEndDate,
}) => {

    const singleCompany = dialogDetails && dialogDetails.length === 1 ? dialogDetails[0] : null
    const earliest = periodStartDate ? moment(periodStartDate).format(DATE_FORMAT) : undefined
    const latest = latestSyncFromDate(periodEndDate)

    const tenantSchema = Yup.object().shape({
      companyId: Yup.string().required('You must select one of the organisations'),
      syncFrom: Yup.string()
        .test('within-period', 'Choose a date inside the report period, no later than today', value => {
          if (!value) return true
          const chosen = moment(value, DATE_FORMAT, true)
          if (!chosen.isValid()) return false
          if (earliest && chosen.isBefore(moment(earliest, DATE_FORMAT))) return false
          return !chosen.isAfter(moment(latest, DATE_FORMAT))
        }),
    })

    const formik = useFormik({
      initialValues: {
        companyId: singleCompany ? singleCompany.companyId : '',
        syncFrom: '',
      },
      enableReinitialize: true,
      validationSchema: tenantSchema,
      onSubmit: (values) => {
        submitHandler(connectionCode, values.companyId, formik.resetForm, values.syncFrom || null);
      }
    });

    const closeHandler = () => {
      formik.resetForm();
      closeDialogHandler()
    }

    return (
      <Dialog open={open} onClose={closeHandler} maxWidth='xs'>
        <Box sx={{ p: 4 }} component='form' onSubmit={formik.handleSubmit}>
          <DialogTitle sx={{ pt: 0, pl: 0, pr: 0 }}>
            {connectionName}
          </DialogTitle>
          <DialogContent sx={{ pt: 0, pl: 0, pr: 0 }}>
            <Typography variant='body1'>
              {singleCompany
                ? `Data will be imported from ${singleCompany.companyName}.`
                : 'Choose which organisation you wish to import data from'}
            </Typography>
            {!singleCompany && dialogDetails && dialogDetails.length > 0 && <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel htmlFor='roles'>Organisation</InputLabel>
              <Select
                value={formik.values.companyId}
                onChange={(e: SelectChangeEvent<string>) => {
                  const value = e.target.value;
                  formik.setFieldValue('companyId', value);
                }}
                error={formik.touched.companyId && Boolean(formik.errors.companyId)}
                fullWidth
                id="companyId"
                name="companyId"
                label='Organisation'
              >
                {dialogDetails.map((company, index) => (
                  <MenuItem key={index} value={company.companyId}>{company.companyName}</MenuItem>
                ))}
              </Select>
              {formik.touched.companyId && formik.errors.companyId &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.companyId}</FormHelperText>
              }
            </FormControl>}
            <TextField
              fullWidth
              sx={{ mt: 2 }}
              label='Read again from (optional)'
              name='syncFrom'
              type='date'
              value={formik.values.syncFrom}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.syncFrom && Boolean(formik.errors.syncFrom)}
              helperText={(formik.touched.syncFrom && formik.errors.syncFrom)
                || 'Leave blank to continue from the last sync. Pick a date to read the ledger again from there: documents whose emissions were deleted come back for review, and anything pending or already counted is left alone.'}
              disabled={isSubmitting}
              inputProps={{ min: earliest, max: latest, 'data-testid': 'sync-from' }}
              InputLabelProps={{ shrink: true }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 0 }}>
            <DialogSaveButtons
              cancelHandler={closeHandler}
              submitHandler={formik.submitForm}
              isSubmitting={isSubmitting}
              buttonInitialText={'Sync Data'}
              buttonLoadingText={'Syncing Data'}
            />
          </DialogActions>
        </Box>
      </Dialog >
    )
  }
